import { ChangeDetectionStrategy, Component, effect, inject, input, OnInit, output, signal } from '@angular/core';

import { ChatRole, MessageContent } from '../../../../models/agent.models';
import { TextHighlighterComponent } from '../../../text-highlighter/text-highlighter';
import { ConversationService } from '../../../../services/conversation.service';
import { buildObjectTTSRequest, extractAudioAndTranscription } from '../chat-message/chat-message.utils';
import { WordData } from '../../../../services/popup.service'; // Corrected import path for WordData
import { USER_DATA_EXCHANGE, UserDataExchangeAbstractService } from '../../../../models/user-data-exchange';
import { ChatUserSettings } from '@dataclouder/ngx-core';

@Component({
  selector: 'dc-message-orchestrator',
  imports: [TextHighlighterComponent],
  templateUrl: './message-orchestrator.component.html',
  styleUrls: ['./message-orchestrator.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageOrchestratorComponent implements OnInit {
  // Services
  private conversationService = inject(ConversationService);
  private userDataExchange = inject<UserDataExchangeAbstractService>(USER_DATA_EXCHANGE);

  // Inputs
  messages = input.required<MessageContent[]>();
  messageRole = input.required<ChatRole>();

  // Outputs
  playAudio = output<MessageContent>();
  audioCompleted = output<MessageContent>();

  // Signals
  messagesSignal = signal<MessageContent[]>([]);

  private messagesEffect = effect(() => {
    // Get the latest messages from the input
    const currentMessages = this.messages();
    if (currentMessages && currentMessages.length > 0) {
      // Update our internal signal with a new array reference
      this.messagesSignal.set([...currentMessages]);
    }
  });

  // Audio queue management
  private audioQueue: number[] = [];
  private isGenerating = false;
  private currentPlayingIndex: number | null = null;
  private preGenerationInProgress = false;

  ngOnInit(): void {
    if (this.messageRole() === ChatRole.Assistant) {
      const chatSettings = this.userDataExchange.getUserChatSettings();

      if (chatSettings.synthVoice) {
        // Initialize the queue with all message indices
        this.initializeAudioQueue();
        // Start processing the queue - generate the first audio
        this.processNextInQueue();
      }
    } else {
      // since user only have one message, just activate signal
      this.changeStates(0, this.messages()[0]);
    }
  }

  // Initialize the queue with all message indices
  private initializeAudioQueue(): void {
    const messages = this.messages();
    if (messages && messages.length > 0) {
      this.audioQueue = messages.map((_, index) => index);
    }
  }

  // Process the next message in the queue
  private async processNextInQueue(): Promise<void> {
    console.log('Processing next message in queue', this.audioQueue);
    if (this.audioQueue.length === 0 || this.isGenerating) {
      return;
    }

    // Get the next index from the queue
    const nextIndex = this.audioQueue.shift()!;

    // Start generating
    this.isGenerating = true;

    // Generate audio for the current message
    try {
      await this.generateAudioForIndex(nextIndex);
    } catch (error) {
      console.error('Error generating audio:', error);
    } finally {
      this.isGenerating = false;
    }

    // If there's another message in the queue, pre-generate it
    this.preGenerateNextIfNeeded();
  }

  // Pre-generate the next audio if available
  private async preGenerateNextIfNeeded(): Promise<void> {
    if (this.audioQueue.length > 0 && !this.preGenerationInProgress) {
      const nextIndex = this.audioQueue[0]; // Peek at next index but don't remove

      console.log('Pre-generating next message in queue', nextIndex, 'queue:', this.audioQueue);

      // Mark pre-generation as in progress
      this.preGenerationInProgress = true;

      // Mark as loading but don't set shouldPlayAudio yet
      const messages = this.messages();
      const message = { ...messages[nextIndex], isLoading: true };
      this.changeStates(nextIndex, message);

      // Generate in background
      const messageAudio = await this.generateAudio(messages[nextIndex], null);
      messageAudio.isLoading = false;
      messageAudio.shouldPlayAudio = false; // Don't auto-play yet
      this.changeStates(nextIndex, messageAudio);

      // Reset pre-generation flag
      this.preGenerationInProgress = false;
    }
  }

  // Generate audio for a specific index
  private async generateAudioForIndex(index: number): Promise<void> {
    const messages = this.messages();

    // Check if this message was already pre-generated (has audioUrl)
    if (messages[index].audioUrl) {
      // If it was pre-generated, just set it to play
      const messageAudio = { ...messages[index], shouldPlayAudio: true };
      this.changeStates(index, messageAudio);
      this.currentPlayingIndex = index;
      return;
    }

    // Otherwise generate it now
    const message = { ...messages[index], isLoading: true };
    this.changeStates(index, message);
    try {
      const messageAudio = await this.generateAudio(messages[index], null);
      messageAudio.isLoading = false;
      messageAudio.shouldPlayAudio = true;
      this.changeStates(index, messageAudio);
    } finally {
      const message = { ...messages[index], isLoading: false };
      this.changeStates(index, message);
    }

    this.currentPlayingIndex = index;
  }

  // Handle audio completion event from text-highlighter
  public onAudioCompleted(message: MessageContent): void {
    // Forward the event
    this.audioCompleted.emit(message);
    this.conversationService.currentAudioStatus.set({ message, completed: true });

    // Reset current playing index
    this.currentPlayingIndex = null;

    // If there are more messages in the queue, process the next one
    if (this.audioQueue.length > 0) {
      this.processNextInQueue();
    }
  }

  public changeStates(index: number, messageAudio: MessageContent): void {
    const messages = this.messages();

    messages[index] = { ...messages[index], ...messageAudio };
    this.messagesSignal.set([...messages]);
  }

  async generateAudio(message: MessageContent, overwriteText: string | null = null): Promise<MessageContent> {
    try {
      const text = overwriteText || message.text || message.content;
      const ttsObject = buildObjectTTSRequest({ ...message, text }, { highlightWords: true } as ChatUserSettings);

      const speechAudio = await this.conversationService.getTTSFile(ttsObject);
      message = extractAudioAndTranscription(message, speechAudio);
      return message;
    } finally {
      this.isGenerating = false;
    }
  }

  public onWordClicked(wordData: WordData): void {
    console.log('Word clicked in MessageOrchestrator:', wordData);
    this.conversationService.notifyWordClicked(wordData);
  }
}
